/**
 * P2-06f · promotion block — config schema, default, admin descriptor, and the
 * pure countdown maths.
 *
 * An in-body promotional banner/strip/split-image (distinct from the site-wide
 * header announcement, P2-01). `template:brandable`. Show/hide scheduling is not
 * here — it piggybacks on `ContentBlock.startsAt/endsAt`, enforced by
 * `getPageBlocks` (P2-05). The optional countdown ticks to `countdown` and
 * hides on expiry / reduced-motion.
 */
import { z } from "zod";

import { blockBackgroundSchema, BLOCK_BACKGROUNDS } from "../backgrounds";
import type { AdminFormField, BlockDefinition } from "../registry";
import { safeHrefSchema } from "./safe-href";

export const PROMO_VARIANTS = ["banner", "strip", "split-image"] as const;
export const PROMO_THEMES = ["light", "dark"] as const;

const promoCtaSchema = z.object({
  label: z.string().min(1).max(60),
  href: safeHrefSchema,
});

/** Background is a brand-token panel or an image (with a scrim over it). */
export const promoBackgroundSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("token"), token: blockBackgroundSchema.default("primary") }),
  z.object({
    kind: z.literal("image"),
    url: z.string().min(1),
    mobileUrl: z.string().min(1).optional(),
    blurDataUrl: z.string().optional(),
    overlayOpacity: z.number().min(0).max(1).default(0.4),
  }),
]);
export type PromoBackground = z.infer<typeof promoBackgroundSchema>;

export const promotionConfigSchema = z.object({
  variant: z.enum(PROMO_VARIANTS).default("banner"),
  headline: z.string().min(1).max(200),
  subcopy: z.string().max(500).optional(),
  cta: promoCtaSchema.optional(),
  background: promoBackgroundSchema.default({ kind: "token", token: "primary" }),
  /** Text colour over an image background (token backgrounds pair their own). */
  theme: z.enum(PROMO_THEMES).default("light"),
  /** ISO 8601 target; the countdown ticks to it and hides once passed. */
  countdown: z.string().datetime().optional(),
  dismissible: z.boolean().default(false),
});
export type PromotionConfig = z.infer<typeof promotionConfigSchema>;

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Time-remaining breakdown, or `null` once the target has passed. Pure so the
 * client ticker and its tests share one implementation.
 */
export function countdownParts(targetMs: number, nowMs: number): CountdownParts | null {
  const diff = targetMs - nowMs;
  if (diff <= 0) return null;
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
}

const promotionAdminForm: AdminFormField[] = [
  { key: "variant", label: "Variant", kind: "select", options: PROMO_VARIANTS },
  { key: "headline", label: "Headline", kind: "text" },
  { key: "subcopy", label: "Subcopy", kind: "textarea" },
  { key: "background", label: "Background", kind: "select", options: BLOCK_BACKGROUNDS },
  { key: "theme", label: "Text theme", kind: "select", options: PROMO_THEMES },
  { key: "countdown", label: "Countdown to", kind: "datetime" },
  { key: "dismissible", label: "Dismissible", kind: "toggle" },
  // `cta` (label + href) is edited by the richer P2-07 editor.
];

export const promotionBlockDefinition = {
  configSchema: promotionConfigSchema,
  defaultConfig: {
    headline: "Sale — up to 40% off",
  } satisfies Partial<PromotionConfig>,
  adminForm: promotionAdminForm,
  // In-body promos usually sit mid-page — below-the-fold; P2-15 lazy-loads.
  eager: false,
  translatablePaths: ["headline", "subcopy", "cta.label"],
} satisfies BlockDefinition;
